/**
 * 🧮 원가분석 공용 조각 — 숫자 칸 · 타일 (v2.235.0 에서 CostAnalysisTab 에서 떼어 냄 · 견적서 [💡 가격 참고] 창도 쓴다)
 */
import { useRef, useState, type ComponentProps } from 'react';

/**
 * 글 칸 — 친 글자를 칸이 직접 들고 있다가 알린다 (v2.247.1 · 사용자 「글씨 쓸 때 글자가 막 밀리는 입력칸」).
 * ⚠원가분석표 값은 react-query 캐시에 있고, 캐시가 화면에 알리는 것은 setTimeout(0) 뒤다(v5 기본).
 *   값을 캐시에서 바로 받으면 React 가 친 직후 칸을 옛 값으로 되돌렸다가 잠시 뒤 새 값을 넣어
 *   커서가 맨 끝으로 튀고(가운데 「abc」 → 「a…bc」) 한글 조합이 깨졌다(「한글」 → 「ㅎ하한한ㄱ그글글」).
 *   칸이 글자를 들고 있으면 되돌림이 없다 · 밖에서 값이 바뀌면(다른 표 · 되돌리기) 그 값으로 맞춘다 — NumCell 과 같은 꼴.
 */
export function TextCell({ value, onChange, ...rest }: Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value: string; onChange: (text: string) => void;
}) {
  const [text, setText] = useState(value);
  const last = useRef(value);
  if (last.current !== value) {
    last.current = value;
    if (text !== value) setText(value);
  }
  return <input {...rest} value={text} onChange={(e) => { setText(e.target.value); onChange(e.target.value); }} />;
}

/** 숫자 칸 — 글자를 그대로 들고 있다가 숫자로 읽히면 알린다(지우고 다시 적을 때 0 이 끼지 않게 · 사이징 툴 NumInput 과 같은 꼴) */
export function NumCell({ value, onChange, className = '', readOnly = false, step = 1, title }: {
  value: number; onChange: (n: number) => void; className?: string; readOnly?: boolean; step?: number | 'any'; title?: string;
}) {
  const [text, setText] = useState(String(value));
  const last = useRef(value);
  if (last.current !== value) {
    last.current = value;
    if (Number(text) !== value) setText(String(value));
  }
  return (
    <input
      type="number"
      inputMode="decimal"
      min={0}
      step={step}
      value={text}
      readOnly={readOnly}
      title={title}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value.trim() === '' || !Number.isFinite(n) || n < 0) return;
        onChange(n);
      }}
      onBlur={() => { if (text.trim() === '' || !Number.isFinite(Number(text)) || Number(text) < 0) setText(String(value)); }}
      // 0 인 칸에 들어가면 0 을 골라 둔다 — 그대로 치면 「032570000」 처럼 앞에 0 이 남았다(값은 맞지만 보기 나쁨 · 10-13 · 견적서에서 온 줄은 원가 0 으로 시작)
      onFocus={(e) => { if (!readOnly && Number(e.target.value) === 0) e.target.select(); }}
      className={`border rounded px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-300 ${readOnly ? 'bg-slate-50 text-slate-600 border-slate-200' : 'bg-white border-slate-300'} ${className}`}
    />
  );
}

export function Tile({ label, hint, value, strong, tone }: { label: string; hint?: string; value: string; strong?: boolean; tone?: 'good' | 'bad' }) {
  const color = tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-emerald-700' : 'text-slate-900';
  return (
    <div className={`rounded border p-3 ${strong ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}>
      <div className="text-xs text-slate-500">{label}{hint && <span className="text-slate-400 ml-1">· {hint}</span>}</div>
      <div className={`text-xl font-bold tabular-nums mt-1 ${color}`}>{value}</div>
    </div>
  );
}
